import { useEffect, useMemo, useState } from 'react'
import {
  createAgentResourceAccess,
  setConnectionCredential,
  createResourceConnection,
  fetchHostPubkey,
  fetchSecrets,
  putSecret,
  type ManifestRuleApproval,
  type ManifestRuleWithStatus,
} from '../../api/ws'
import { base64Decode, base64Encode, encryptToHostPubkey } from '../../lib/secretEnvelope'
import { useAppStore } from '../../store/app'
import type { PluginCredentialSpec, PluginInstall } from '../../store/types'
import { foreignHostWarning } from './foreignHostWarning'
import { InstallWizard } from './InstallWizard'
import {
  AuthSchemeBadge,
  authSchemeExplainer,
  buildConfigJSON,
  ConfigStep,
  CredentialStep,
  DetailsStep,
  OAuthConnectStep,
  oauthProviderFromSpecs,
  parseConfigSchema,
  parseCredentialSpecs,
  slugify,
} from './wizardParts'

// NewConnectionWizard is a multi-step sheet that takes an operator from
// zero to a fully wired agent → resource connection in one flow:
//
//   pick plugin → name connection → config fields →
//   enter credentials (one step per secret) → approve rules
//
// On completion: resource_connection + secret(s) filed as its credential(s)
// are created, then a single agent_resource_access_create writes the
// rules + binding + secret.use atomically (and cycles/announces to the
// running agent). The caller receives onDone() with no arguments — it
// just refreshes.
//
// This is the per-agent (Surface B) flow. The household-level "connect an
// account" flow lives in ConnectAccountWizard and shares the step
// components via wizardParts.

type Step =
  | 'plugin'
  | 'details'
  | 'config'
  | { kind: 'cred'; index: number }
  | 'rules'
  | 'oauth'

export function NewConnectionWizard({
  agentInstanceID,
  hostID,
  pluginInstalls,
  onDone,
  onCancel,
}: {
  agentInstanceID: string
  hostID: string
  pluginInstalls: PluginInstall[]
  onDone: () => void
  onCancel: () => void
}) {
  const allSecrets = useAppStore((s) => s.secrets)
  const hosts = useAppStore((s) => s.hosts)
  const humanUserID = useAppStore((s) => s.humanUserID)

  const [pickedInstallID, setPickedInstallID] = useState<string>(
    pluginInstalls.length === 1 ? pluginInstalls[0].id : '',
  )
  const [connectionSlug, setConnectionSlug] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [configValues, setConfigValues] = useState<Record<string, string>>({})
  // credValues maps credIndex → plaintext token (cleared after submission)
  const [credValues, setCredValues] = useState<Map<number, string>>(new Map())
  // credSecretIDs maps credIndex → existing secret ID when user picks one
  const [credSecretIDs, setCredSecretIDs] = useState<Map<number, string>>(new Map())

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pickedInstall = useMemo(
    () => pluginInstalls.find((p) => p.id === pickedInstallID) ?? null,
    [pluginInstalls, pickedInstallID],
  )
  const configSchema = useMemo(
    () => parseConfigSchema(pickedInstall?.config_schema),
    [pickedInstall],
  )
  const credSpecs = useMemo(
    () => parseCredentialSpecs(pickedInstall?.credential_specs),
    [pickedInstall],
  )
  const secretCreds = useMemo(() => credSpecs.filter((c) => c.secret), [credSpecs])
  // Provider slug from the manifest's `type: oauth2_<provider>`, not hardcoded.
  const oauthProvider = useMemo(() => oauthProviderFromSpecs(credSpecs), [credSpecs])
  const manifestDefaults = useMemo(
    () => buildManifestDefaults(pickedInstall),
    [pickedInstall],
  )

  const isOAuthPlugin = pickedInstall?.auth_scheme === 'oauth2_user'

  // Build the ordered step list from the picked plugin.
  const steps: Step[] = useMemo(() => {
    const s: Step[] = []
    if (pluginInstalls.length > 1) s.push('plugin')
    s.push('details')
    if (configSchema?.properties && Object.keys(configSchema.properties).length > 0) {
      s.push('config')
    }
    if (!isOAuthPlugin) {
      secretCreds.forEach((_, i) => s.push({ kind: 'cred', index: i }))
    }
    s.push('rules')
    if (isOAuthPlugin) s.push('oauth')
    return s
  }, [pluginInstalls.length, configSchema, secretCreds, isOAuthPlugin])

  const hostSecrets = useMemo(
    () => allSecrets.filter((s) => s.host_id === hostID),
    [allSecrets, hostID],
  )

  // Fetch secrets once we reach a credential step so the "use existing"
  // list is populated. Fires at most once per wizard instance.
  const hasCredStep = steps.some((s) => typeof s === 'object' && s.kind === 'cred')
  useEffect(() => {
    if (hasCredStep) fetchSecrets()
  }, [hasCredStep])

  const [stepIndex, setStepIndex] = useState(0)
  const currentStep = steps[stepIndex] ?? 'rules'
  // Holds the server-assigned connection ID after the rules step so the
  // oauth step can pass it to begin_oauth without re-creating the connection.
  const [pendingConnID, setPendingConnID] = useState<string | null>(null)
  // Approved rules held between the rules step and the OAuth callback, so
  // the deferred access write (agent_resource_access_create) in
  // onConnected has them once the connection is authorized.
  const [pendingRules, setPendingRules] = useState<ManifestRuleApproval[] | null>(null)

  function advance() {
    setError(null)
    setStepIndex((i) => Math.min(i + 1, steps.length - 1))
  }

  function retreat() {
    setError(null)
    setStepIndex((i) => Math.max(i - 1, 0))
  }

  function handlePickPlugin(installID: string) {
    setPickedInstallID(installID)
    // Suggest a slug from the plugin slug
    const install = pluginInstalls.find((p) => p.id === installID)
    if (install && !connectionSlug) {
      setConnectionSlug(slugify(install.plugin_slug))
    }
    setConfigValues({})
    setCredValues(new Map())
    advance()
  }

  function handleDetailsNext(e: React.FormEvent) {
    e.preventDefault()
    if (!connectionSlug.trim()) return
    advance()
  }

  function handleConfigNext(e: React.FormEvent) {
    e.preventDefault()
    advance()
  }

  function handleCredNext(e: React.FormEvent) {
    e.preventDefault()
    advance()
  }

  // Plaintext-trust gate (docs/secret-exposure-model.md), mirroring
  // ConnectAccountWizard.confirmForeignHost(). A credential filed here is
  // sealed to the agent's host and is decryptable there, so if that host's
  // owner isn't the signed-in user, they can read its plaintext value. Confirm
  // before creating the connection — this covers BOTH the OAuth path
  // (begin_oauth checks only org membership, so the token would seal on the
  // foreign host silently) and the new-secret path (server-blocked by
  // userOwnsHost, but a confirm is friendlier than a raw rejection). Own host
  // (or unknown owner) → no prompt. The grantor is the exposed party and
  // initiates, so a client gate is the right level. Returns true to proceed.
  function confirmForeignHost(): boolean {
    const warning = foreignHostWarning(
      hosts.find((h) => h.host_id === hostID),
      humanUserID,
    )
    return !warning || confirm(warning)
  }

  async function handleRulesApprove(rules: ManifestRuleApproval[]) {
    if (!pickedInstall) return
    if (!confirmForeignHost()) return
    setBusy(true)
    setError(null)
    const connSlug = connectionSlug.trim()
    try {
      // 1. Create the connection; server assigns a UUID and returns it
      //    along with the final (possibly de-conflicted) slug.
      const connResp = await createResourceConnection({
        slug: connSlug,
        pluginInstallID: pickedInstall.id,
        displayName: displayName.trim() || undefined,
        config: buildConfigJSON(configSchema, configValues),
      })
      if (connResp?.error) {
        setError(connResp.error)
        return
      }
      // Use the server-confirmed slug (may differ if de-conflicted) and
      // the server-generated UUID for downstream calls.
      const confirmedSlug = connResp.slug ?? connSlug
      const connID = connResp.id!

      // 2. For each secret credential: either reuse an existing secret or
      //    encrypt + store a new one, then file it on the connection under
      //    the manifest's credential name.
      const needsNewSecret = secretCreds.some((_, i) => !credSecretIDs.has(i) && !!credValues.get(i))
      let rawPub: Uint8Array | null = null
      if (needsNewSecret) {
        const pubResp = await fetchHostPubkey(hostID)
        if (pubResp.error || !pubResp.pubkey) {
          setError(
            pubResp.error ??
              'Host has no encryption key. Run any `hearth secret` command on the host first.',
          )
          return
        }
        rawPub = base64Decode(pubResp.pubkey)
      }

      for (let i = 0; i < secretCreds.length; i++) {
        const spec = secretCreds[i]
        let secretID: string | null = credSecretIDs.get(i) ?? null

        if (!secretID) {
          const plaintext = credValues.get(i)
          if (!plaintext) continue

          const secretName = `${confirmedSlug}.${spec.name}`
          const envelope = encryptToHostPubkey(rawPub!, new TextEncoder().encode(plaintext))
          const ciphertextB64 = base64Encode(envelope)

          const putResp = await putSecret({
            hostID,
            name: secretName,
            ciphertextB64,
            purpose: `${spec.name} for ${connID}`,
          })
          if (putResp.error) {
            setError(`Secret ${spec.name}: ${putResp.error}`)
            return
          }
          secretID = putResp.id!
        }

        const credResp = await setConnectionCredential({
          resourceConnectionID: connID,
          secretID,
          name: spec.name,
        })
        if (credResp.error) {
          setError(`Credential ${spec.name}: ${credResp.error}`)
          return
        }
      }

      // 3. Grant the agent access. Route through the atomic access RPC
      //    (agent_resource_access_create) — the same path the Household
      //    Resource Connections sheet uses — so rules + binding +
      //    secret.use land in one transaction and the server cycles the
      //    running agent and emits the resource_granted system event.
      //
      //    For oauth2_user plugins the credential is filed asynchronously
      //    by the OAuth callback, so there is nothing to bind (or
      //    announce) yet: stash the approved rules + connection and defer
      //    the whole access write to onConnected below.
      if (isOAuthPlugin) {
        setPendingConnID(connID)
        setPendingRules(rules)
        advance()
        return
      }

      const accessResp = await createAgentResourceAccess({
        resourceConnectionID: connID,
        aiAgentInstanceIDs: [agentInstanceID],
        rules,
        scope: 'agent',
      })
      if (accessResp.error) {
        setError(accessResp.error)
        return
      }

      onDone()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  const stepLabel = stepLabelFor(currentStep, secretCreds)

  return (
    <div className="resources__picker-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="resources__picker resources__picker--wizard">
        {/* Pinned header — never scrolls, so iOS can't push it off-screen
            when it auto-scrolls the document to keep an input visible. */}
        <div className="resources__picker-header">
          {stepIndex > 0 && (
            <button
              onClick={retreat}
              disabled={busy}
              className="resources__picker-back"
            >
              ‹
            </button>
          )}
          <div className="resources__picker-title">
            New Connection — {stepLabel}
          </div>
        </div>

        {/* Auth scheme explainer — shown when a plugin is picked and has an auth_scheme */}
        {pickedInstall?.auth_scheme && currentStep !== 'plugin' && (
          <div className="resources__auth-scheme-banner">
            <AuthSchemeBadge scheme={pickedInstall.auth_scheme} />
            <span className="resources__auth-scheme-banner-text">
              {authSchemeExplainer(pickedInstall.auth_scheme)}
            </span>
          </div>
        )}

        {/* Scrollable body — inputs live here so iOS scroll is contained */}
        <div className="resources__picker-body">
          {error && (
            <div style={{ padding: '8px 12px', background: '#fee', color: '#900', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          {currentStep === 'plugin' && (
            <PluginPickStep
              installs={pluginInstalls}
              onPick={handlePickPlugin}
              disabled={busy}
            />
          )}

          {currentStep === 'details' && (
            <DetailsStep
              connectionSlug={connectionSlug}
              displayName={displayName}
              onChangeSlug={setConnectionSlug}
              onChangeName={setDisplayName}
              onNext={handleDetailsNext}
              disabled={busy}
            />
          )}

          {currentStep === 'config' && configSchema && (
            <ConfigStep
              schema={configSchema}
              values={configValues}
              onChange={setConfigValues}
              onNext={handleConfigNext}
              disabled={busy}
            />
          )}

          {typeof currentStep === 'object' && currentStep.kind === 'cred' && (
            <CredentialStep
              spec={secretCreds[currentStep.index]}
              credIndex={currentStep.index}
              totalSecretCreds={secretCreds.length}
              value={credValues.get(currentStep.index) ?? ''}
              onChange={(v) => {
                setCredValues((m) => new Map(m).set(currentStep.index, v))
                setCredSecretIDs((m) => { const n = new Map(m); n.delete(currentStep.index); return n })
              }}
              existingSecrets={hostSecrets}
              pickedSecretID={credSecretIDs.get(currentStep.index) ?? null}
              onPickSecret={(id) => {
                setCredSecretIDs((m) => new Map(m).set(currentStep.index, id))
                setCredValues((m) => { const n = new Map(m); n.delete(currentStep.index); return n })
              }}
              onNext={handleCredNext}
              disabled={busy}
            />
          )}

          {currentStep === 'rules' && (
            <div>
              {manifestDefaults.length === 0 ? (
                <p style={{ opacity: 0.6, fontSize: 14 }}>
                  This plugin has no default rules. The connection will be created with no
                  agent permissions; add rules via the Rules editor after setup.
                </p>
              ) : null}
              <InstallWizard
                manifestDefaults={manifestDefaults}
                scopeLabel="for this agent"
                submitLabel="Create connection & grant access"
                busy={busy}
                onApprove={handleRulesApprove}
                onCancel={onCancel}
              />
            </div>
          )}

          {currentStep === 'oauth' && pendingConnID && pickedInstall && (
            <OAuthConnectStep
              connectionID={pendingConnID}
              hostID={hostID}
              provider={oauthProvider}
              pluginDisplayName={pickedInstall.display_name ?? pickedInstall.plugin_slug}
              onConnected={async () => {
                // The connection now holds a credential — write rules +
                // binding + secret.use atomically and let the server cycle
                // + announce.
                const accessResp = await createAgentResourceAccess({
                  resourceConnectionID: pendingConnID,
                  aiAgentInstanceIDs: [agentInstanceID],
                  rules: pendingRules ?? [],
                  scope: 'agent',
                })
                if (accessResp.error) {
                  setError(accessResp.error)
                  return
                }
                onDone()
              }}
              onError={setError}
            />
          )}

          {currentStep !== 'rules' && currentStep !== 'plugin' && currentStep !== 'oauth' && (
            <button
              className="resources__picker-cancel"
              style={{ marginTop: 12 }}
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function PluginPickStep({
  installs,
  onPick,
  disabled,
}: {
  installs: PluginInstall[]
  onPick: (id: string) => void
  disabled: boolean
}) {
  return (
    <div className="resources__picker-list">
      {installs.map((p) => (
        <button
          key={p.id}
          className="resources__picker-item"
          onClick={() => onPick(p.id)}
          disabled={disabled}
        >
          <div className="resources__picker-item-title">
            {p.display_name || p.plugin_slug}
            {p.auth_scheme && <AuthSchemeBadge scheme={p.auth_scheme} />}
          </div>
          <div className="resources__picker-item-meta">
            {p.plugin_slug} · {p.version ?? 'no version'}
          </div>
        </button>
      ))}
    </div>
  )
}

function stepLabelFor(step: Step, secretCreds: PluginCredentialSpec[]): string {
  if (step === 'plugin') return 'Choose plugin'
  if (step === 'details') return 'Name connection'
  if (step === 'config') return 'Config'
  if (typeof step === 'object' && step.kind === 'cred') {
    const spec = secretCreds[step.index]
    return spec ? spec.name : `Credential ${step.index + 1}`
  }
  if (step === 'oauth') return 'Connect account'
  return 'Approve rules'
}

function buildManifestDefaults(install: PluginInstall | null): ManifestRuleWithStatus[] {
  if (!install?.default_rules) return []
  try {
    const rules = JSON.parse(install.default_rules) as Array<{
      action: string
      decision: string
      when?: Record<string, unknown>
    }>
    return rules.map((r) => ({
      action: r.action,
      decision: r.decision as 'allow' | 'deny' | 'ask',
      when: r.when,
      already_minted: false,
    }))
  } catch {
    return []
  }
}
